import React from 'react';
import PageLayout from '../components/PageLayout';
import { useSEO } from '../hooks/useSEO';
import { CONTACT_EMAIL } from '../config';

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="glass-card p-5 space-y-2">
    <p className="text-white/90 font-semibold">{title}</p>
    <div className="text-white/55 text-sm leading-relaxed space-y-2">{children}</div>
  </div>
);

const PrivacyPolicy: React.FC = () => {
  useSEO(
    'Privacy Policy — Purgr',
    'Purgr processes your Instagram files entirely in your browser. No data is uploaded, stored, or shared. No Instagram login required.'
  );

  return (
    <PageLayout title="Privacy Policy">
      <div className="space-y-4">
        <p className="text-white/50 text-xs mb-6">Last updated: March 2025</p>

        <Section title="The short version">
          <p>
            Purgr processes your Instagram files <strong className="text-white/80">entirely inside your browser</strong>.
            Your files are never uploaded to any server. We collect no personal data. No Instagram login is ever required.
          </p>
        </Section>

        <Section title="1. What we collect">
          <p>We collect nothing. Purgr does not have a backend server or database.</p>
          <p>
            The only data stored anywhere is a single key in your browser's <code className="text-white/70 bg-white/5 px-1 rounded">localStorage</code> — <code className="text-white/70 bg-white/5 px-1 rounded">purgr_cookie_consent</code> — which records whether you accepted or declined cookies. This value never leaves your device.
          </p>
        </Section>

        <Section title="2. Your Instagram files">
          <p>
            When you upload your <code className="text-white/70 bg-white/5 px-1 rounded">following.json</code> and <code className="text-white/70 bg-white/5 px-1 rounded">followers_1.json</code> files, they are read using the browser's built-in <strong className="text-white/80">FileReader API</strong> — entirely on your device.
          </p>
          <ul className="space-y-1 list-disc list-inside">
            <li>Your files are <strong className="text-white/80">never uploaded</strong> to any server.</li>
            <li>Your files are <strong className="text-white/80">never stored</strong> — not in localStorage, not in cookies, not anywhere.</li>
            <li>Your file data exists only in your browser's memory and is gone the moment you close or refresh the tab.</li>
          </ul>
        </Section>

        <Section title="3. No Instagram login required">
          <p>
            Purgr does not ask for your Instagram username, password, or any account credentials. We have no connection to your Instagram account. We use only the data files that Instagram provides to you via their official data export feature.
          </p>
        </Section>

        <Section title="4. Cookies and localStorage">
          <p>
            Purgr uses a single <code className="text-white/70 bg-white/5 px-1 rounded">localStorage</code> entry (<code className="text-white/70 bg-white/5 px-1 rounded">purgr_cookie_consent</code>) to remember your cookie preference. No cookies are set unless you accept the cookie banner. If you decline, only that preference is stored.
          </p>
          <p>
            If you accept cookies, Google AdSense may set its own cookies to serve personalized ads. See Google's Privacy Policy for details on how AdSense handles data.
          </p>
        </Section>

        <Section title="5. Advertising">
          <p>
            Purgr uses Google AdSense to display advertisements. If you accept cookies, AdSense may serve
            personalized ads based on your browsing history. If you decline cookies, non-personalized ads may
            be shown instead.
          </p>
          <p>
            Third-party vendors, including Google, use cookies to serve ads based on your prior visits to
            this website or other websites. Google's use of advertising cookies enables it and its partners
            to serve ads to you based on your visit to Purgr and/or other sites on the internet.
          </p>
          <p>
            You may opt out of personalized advertising by visiting{' '}
            <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-white/90 underline">Google Ads Settings</a>
            {' '}or{' '}
            <a href="https://optout.aboutads.info/" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-white/90 underline">optout.aboutads.info</a>.
          </p>
          <p>
            Google's advertising services are governed by{' '}
            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-white/90 underline">Google's Privacy Policy</a>.
          </p>
        </Section>

        <Section title="6. Third-party links">
          <p>
            Purgr contains links to Ko-fi, Gumroad, and Instagram growth tools (Later, Kicksta, Planoly). These are affiliate links — we may earn a small commission if you sign up through them, at no additional cost to you. Each of these services has its own privacy policy that governs how they handle your data.
          </p>
        </Section>

        <Section title="7. Children's privacy">
          <p>
            Purgr is not directed at children under 13. We do not knowingly collect any information from children.
          </p>
        </Section>

        <Section title="8. Changes to this policy">
          <p>
            We may update this Privacy Policy from time to time. We'll update the "last updated" date at the top of this page. Continued use of Purgr after changes constitutes acceptance of the updated policy.
          </p>
        </Section>

        <Section title="9. Contact">
          <p>
            Questions about this policy? Reach out at{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-white/60 hover:text-white/90 underline">
              {CONTACT_EMAIL}
            </a>
          </p>
        </Section>
      </div>
    </PageLayout>
  );
};

export default PrivacyPolicy;
